import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/api/client";
import { EMOJIS } from "@/lib/constants";
import logoEnglputzeder from "@/assets/logo-englputzeder.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Check, Users, Plus, Camera, Upload, Lock } from "lucide-react";
import { createGroup, joinGroup, lookupGroup, requestJoinGroup } from "@/lib/groups";
import { uploadAvatar, MAX_AVATAR_BYTES } from "@/lib/avatar";
import { UserAvatar } from "@/components/UserAvatar";
import { GroupLogo } from "@/components/GroupLogo";
import { PlayerCombobox } from "@/components/PlayerCombobox";
import { loadGroupTeams, getWMStart, isBonusLocked, checkUsernameAvailable, checkGroupNameAvailable, BONUS_MAX_POINTS, AT_PROGRESSION_OPTIONS, type GroupTeam } from "@/lib/bonus";
import type { AtProgression } from "@/api/client";
import { teamDe } from "@/lib/teamNames";
import { PasswordRequirements, validatePassword } from "@/components/PasswordRequirements";

export default function Onboarding() {
  const { user, profile, loading, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const urlCode = (searchParams.get("code") || "").toUpperCase().slice(0, 6);
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [username, setUsername] = useState("");
  const [emoji, setEmoji] = useState("⚽");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarTab, setAvatarTab] = useState<"emoji" | "photo">("emoji");
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  // Group step
  const [groupMode, setGroupMode] = useState<"join" | "create" | null>(null);
  const [groupCode, setGroupCode] = useState("");
  const [groupPreview, setGroupPreview] = useState<{ name: string; logo_url: string | null; member_count: number } | null>(null);
  const [groupCheck, setGroupCheck] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState("");

  // Bonus
  const [worldChamp, setWorldChamp] = useState("");
  const [topScorer, setTopScorer] = useState("");
  const [groupWinners, setGroupWinners] = useState<Record<string, string>>({});
  const [groupTeams, setGroupTeams] = useState<Record<string, GroupTeam[]>>({});
  const [wmStart, setWmStart] = useState<string | null>(null);
  const bonusLocked = isBonusLocked(wmStart);
  // Austria-themed extras (same shape as Bonus page).
  const [atBetterThanDe, setAtBetterThanDe] = useState<"" | "yes" | "no">("");
  const [atGroupPosition, setAtGroupPosition] = useState<"" | "1" | "2" | "3" | "4">("");
  const [atFirstGoalScorer, setAtFirstGoalScorer] = useState("");
  const [atTopScorer, setAtTopScorer] = useState("");
  const [atProgression, setAtProgression] = useState<AtProgression | "">("");
  const [atGoalsTotal, setAtGoalsTotal] = useState<string>("");

  // Username availability
  const [usernameStatus, setUsernameStatus] = useState<"idle" | "checking" | "ok" | "taken">("idle");
  // Group name availability
  const [groupNameStatus, setGroupNameStatus] = useState<"idle" | "checking" | "ok" | "taken">("idle");

  useEffect(() => {
    loadGroupTeams().then(setGroupTeams);
    getWMStart().then(setWmStart);
  }, []);

  // Live username check
  useEffect(() => {
    if (!/^[A-Za-z0-9_]{3,20}$/.test(username)) { setUsernameStatus("idle"); return; }
    if (profile?.username && profile.username.toLowerCase() === username.toLowerCase()) { setUsernameStatus("ok"); return; }
    setUsernameStatus("checking");
    const t = setTimeout(async () => {
      const ok = await checkUsernameAvailable(username);
      setUsernameStatus(ok ? "ok" : "taken");
    }, 350);
    return () => clearTimeout(t);
  }, [username, profile?.username]);

  // Live group name check
  useEffect(() => {
    const trimmed = newGroupName.trim();
    if (trimmed.length < 2) { setGroupNameStatus("idle"); return; }
    setGroupNameStatus("checking");
    const t = setTimeout(async () => {
      const ok = await checkGroupNameAvailable(trimmed);
      setGroupNameStatus(ok ? "ok" : "taken");
    }, 350);
    return () => clearTimeout(t);
  }, [newGroupName]);

  useEffect(() => { document.title = "Willkommen – Englputzeder Tippspiel"; }, []);
  useEffect(() => {
    if (profile?.username) setUsername(profile.username);
    if (profile?.emoji) setEmoji(profile.emoji);
    if (profile?.avatarUrl) { setAvatarUrl(profile.avatarUrl); setAvatarTab("photo"); }
    if (profile?.bonusWinner) setWorldChamp(profile.bonusWinner);
    if (profile?.hasPassword && step === 0) setStep(1);
    if (profile && !profile.hasPassword) setStep(0);
  }, [profile]);

  // Pre-fill group code from URL
  useEffect(() => {
    if (urlCode && urlCode.length === 6 && !groupCode) {
      setGroupMode("join");
      setGroupCode(urlCode);
    }
  }, [urlCode]);

  // Auto-file the join request as soon as we have a previewed group from
  // the URL invite code, so step 2 lands in a "Anfrage gesendet" state
  // immediately — user just confirms with "Weiter →" without re-entering
  // the code. Idempotent server-side: a duplicate request returns the
  // existing "pending" status without erroring.
  const [inviteRequestStatus, setInviteRequestStatus] = useState<"idle" | "pending" | "already_member" | "error">("idle");
  const inviteAutoFiredRef = useRef(false);
  useEffect(() => {
    if (!user || !urlCode || urlCode.length !== 6) return;
    if (!groupPreview) return;
    if (inviteAutoFiredRef.current) return;
    inviteAutoFiredRef.current = true;
    (async () => {
      try {
        const res = await requestJoinGroup(urlCode);
        setInviteRequestStatus(res.status === "already_member" ? "already_member" : "pending");
      } catch (err) {
        setInviteRequestStatus("error");
        toast.error(err instanceof Error ? err.message : "Anfrage konnte nicht gesendet werden");
      }
    })();
  }, [user, urlCode, groupPreview]);

  // Live group code lookup
  useEffect(() => {
    setGroupPreview(null);
    setGroupError(null);
    const code = groupCode.trim().toUpperCase();
    if (code.length !== 6) return;
    setGroupCheck(true);
    const handle = setTimeout(async () => {
      const found = await lookupGroup(code);
      setGroupCheck(false);
      if (!found) { setGroupError("Code nicht gefunden"); return; }
      setGroupPreview(found);
    }, 400);
    return () => clearTimeout(handle);
  }, [groupCode]);

  if (!loading && !user) return <Navigate to="/login" replace />;
  if (!loading && profile?.bonusWinner && profile?.hasPassword) return <Navigate to="/" replace />;

  const validUsername = /^[A-Za-z0-9_]{3,20}$/.test(username) && usernameStatus !== "taken";
  const validPassword = validatePassword(password).ok && password === confirm;

  const savePassword = async () => {
    if (!user || !validPassword) return;
    setBusy(true);
    try {
      await api.auth.setPassword(password);
      if (username) await api.profiles.update({ username });
      await refreshProfile();
      toast.success("Passwort gesetzt ✅");
      setStep(1);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const submitGroup = async () => {
    if (!groupMode) { setStep(3); return; }
    setBusy(true);
    try {
      if (groupMode === "join") {
        if (!groupPreview) throw new Error("Bitte gültigen Code eingeben");
        // joinGroup actually files a pending request — group admin still
        // needs to approve. Toast wording reflects that so the user
        // doesn't expect to see themselves in the leaderboard yet.
        await joinGroup(groupCode);
        toast.success(`⏳ Anfrage an „${groupPreview.name}" gesendet — Admin muss bestätigen.`, { duration: 6000 });
      } else {
        if (newGroupName.trim().length < 2) throw new Error("Gruppenname zu kurz");
        await createGroup(newGroupName.trim());
        toast.success(`🚀 Gruppe „${newGroupName}" erstellt — du bist Admin`);
      }
      confetti({ particleCount: 80, spread: 75, origin: { y: 0.5 } });
      setStep(3);
    } catch (e: any) {
      toast.error(e.message ?? "Fehler");
    }
    setBusy(false);
  };

  const finish = async () => {
    if (!user || !worldChamp) return;
    setBusy(true);
    try {
      await api.profiles.update({ username, emoji, bonusWinner: worldChamp, onboardingCompleted: true });
    } catch (err) {
      setBusy(false);
      const msg = err instanceof ApiError ? err.message : "Fehler";
      toast.error(msg.includes("username_taken") ? "Username bereits vergeben" : msg);
      return;
    }
    try {
      await api.bonus.upsert({
        worldChampion: worldChamp || null,
        topScorer: topScorer || null,
        groupWinners,
        atBetterThanDe: atBetterThanDe === "" ? null : atBetterThanDe === "yes",
        atGroupPosition: atGroupPosition === "" ? null : Number(atGroupPosition),
        atFirstGoalScorer: atFirstGoalScorer || null,
        atTopScorer: atTopScorer || null,
        atProgression: atProgression || null,
        atGoalsTotal: atGoalsTotal === "" ? null : Math.max(0, Math.min(99, Number(atGoalsTotal))),
      });
    } catch (err) {
      // Bonus is non-fatal — proceed even if it failed (e.g. WM-Start lock)
      console.warn(err);
    }
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
    await refreshProfile();
    toast.success(`Willkommen, ${username}! 🎉`);
    setBusy(false);
    nav("/", { replace: true });
  };

  // Skip handler — only available once a password is set (step >= 1). Marks
  // onboarding complete with whatever the user has entered so far and goes
  // straight to the dashboard. The bonus question is then editable later via
  // Profil → "Bonus-Fragen".
  const skipOnboarding = async () => {
    if (step < 1) return;
    setBusy(true);
    try {
      await api.profiles.update({
        username: username || profile?.username,
        emoji: emoji || profile?.emoji,
        onboardingCompleted: true,
      });
      await refreshProfile();
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Überspringen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-primary text-primary-foreground py-4 text-center relative pt-[calc(env(safe-area-inset-top)+1rem)]">
        {/* Englputzeder logo + wordmark, matching the Login screen. */}
        <img
          src={logoEnglputzeder}
          alt="Englputzeder"
          className="h-7 w-auto mx-auto mb-1.5 bg-white rounded-md px-1.5 py-0.5 inline-block"
        />
        <div className="font-display font-extrabold text-xl">
          Tipp<span className="bg-accent text-accent-foreground px-1.5 rounded-md">spiel</span>
        </div>
        {/* Skip — only after the password step is past. We don't allow
            skipping step 0 because the user needs a way to log back in. */}
        {step >= 1 && (
          <button
            onClick={skipOnboarding}
            disabled={busy}
            className="absolute right-3 top-3 text-[11px] font-bold text-primary-foreground/80 hover:text-primary-foreground underline tap disabled:opacity-50"
            type="button"
          >
            Überspringen →
          </button>
        )}
      </header>

      <div className="flex-1 container max-w-md py-6">
        <div className="flex gap-1 mb-6">
          {[0,1,2,3,4].map(i => (
            <div key={i} className={`h-1 flex-1 rounded-pill ${i <= step ? "bg-accent" : "bg-muted"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="0" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}} className="space-y-4">
              <div className="rounded-md bg-success/10 border border-success/30 p-3 text-sm">
                ✅ <b>E-Mail bestätigt!</b> Willkommen beim Englputzeder Tippspiel 🎉
              </div>
              <h1 className="font-display font-extrabold text-3xl">Passwort festlegen 🔐</h1>
              <p className="text-muted-foreground">Damit du dich beim nächsten Mal direkt einloggen kannst — ohne Mail-Bestätigung.</p>
              <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Passwort" className="h-14 text-lg font-semibold" />
              {password.length > 0 && <PasswordRequirements password={password} />}
              <Input type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} placeholder="Passwort bestätigen" className="h-14 text-lg font-semibold" />
              {confirm.length > 0 && password !== confirm && <p className="text-xs text-destructive">Passwörter stimmen nicht überein.</p>}
              <Button disabled={!validPassword || busy} onClick={savePassword} className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                {busy ? "Speichere …" : "Passwort speichern → Weiter"}
              </Button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="1" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}} className="space-y-4">
              <h1 className="font-display font-extrabold text-3xl">Wie heißt du? 👋</h1>
              <p className="text-muted-foreground">3–20 Zeichen, Buchstaben/Zahlen/Unterstrich.</p>
              <Input value={username} onChange={(e)=>setUsername(e.target.value.replace(/\s/g,""))} placeholder="z.B. ToniTipper" className="h-14 text-lg font-semibold" maxLength={20} />
              {username.length >= 3 && (
                usernameStatus === "checking" ? <p className="text-xs text-muted-foreground">Prüfe Verfügbarkeit …</p>
                : usernameStatus === "taken" ? <p className="text-xs text-destructive">❌ Benutzername bereits vergeben</p>
                : usernameStatus === "ok" ? <p className="text-xs text-success">✅ Verfügbar</p>
                : null
              )}
              <Button disabled={!validUsername || usernameStatus === "checking"} onClick={()=>setStep(2)} className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">Weiter →</Button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="2" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}} className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="h-7 w-7 text-accent-foreground bg-accent rounded-md p-1" />
                <h1 className="font-display font-extrabold text-3xl">Tippgruppe</h1>
              </div>
              <p className="text-muted-foreground">Tippt mit Freunden, Kollegen oder dem Verein. <b>Optional</b> — du kannst auch alleine tippen.</p>

              {/* Invite-link path: a 6-letter code arrived in the URL. We
                  auto-file the join request and surface a pending-confirm
                  card instead of asking the user to re-enter the code or
                  pick a mode. Mode-picker UI stays available only when
                  the user landed here without an invite. */}
              {urlCode && urlCode.length === 6 && (
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                    Einladung erkannt
                  </div>
                  {groupPreview ? (
                    <div className="rounded-lg border-2 border-accent bg-accent/10 p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <GroupLogo url={groupPreview.logo_url} size={48} />
                        <div className="min-w-0">
                          <div className="font-display font-bold text-base truncate">{groupPreview.name}</div>
                          <div className="text-xs text-muted-foreground">{groupPreview.member_count} Mitglieder</div>
                        </div>
                      </div>
                      {inviteRequestStatus === "pending" && (
                        <div className="text-sm bg-card rounded-md px-3 py-2 border border-border">
                          ⏳ <b>Deine Anfrage wurde gesendet</b> — sobald ein Admin der Gruppe sie bestätigt, bist du dabei.
                        </div>
                      )}
                      {inviteRequestStatus === "already_member" && (
                        <div className="text-sm bg-card rounded-md px-3 py-2 border border-border">
                          ✅ Du bist bereits Mitglied dieser Gruppe.
                        </div>
                      )}
                      {inviteRequestStatus === "idle" && (
                        <div className="text-xs text-muted-foreground">Sende Anfrage …</div>
                      )}
                      {inviteRequestStatus === "error" && (
                        <div className="text-sm bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2 text-destructive">
                          Anfrage konnte nicht gesendet werden. Du kannst den Code später unter Gruppen erneut versuchen.
                        </div>
                      )}
                    </div>
                  ) : groupError ? (
                    <div className="rounded-lg border-2 border-destructive/40 bg-destructive/5 p-4 text-sm">
                      <b>Code „{urlCode}" nicht gefunden.</b> Frag deine:n Einladende:n nach einem aktuellen Link, oder überspringe diesen Schritt.
                    </div>
                  ) : (
                    <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">Lade Gruppe …</div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep(3)} className="flex-1 h-12">
                      Weiter →
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Tipp: Du kannst zusätzlich später unter <b>Gruppen</b> weiteren Gruppen beitreten oder selbst eine gründen.
                  </p>
                </div>
              )}

              {/* Manual mode picker — only shown when there's no invite code */}
              {!urlCode && !groupMode && (
                <div className="grid grid-cols-1 gap-2">
                  <button onClick={()=>setGroupMode("join")} className="rounded-lg border-2 border-border bg-card p-4 text-left hover:border-accent transition tap">
                    <div className="font-display font-bold flex items-center gap-2">🎟️ Code eingeben</div>
                    <div className="text-xs text-muted-foreground mt-1">Du hast einen 6-stelligen Einladungscode bekommen</div>
                  </button>
                  <button onClick={()=>setGroupMode("create")} className="rounded-lg border-2 border-border bg-card p-4 text-left hover:border-accent transition tap">
                    <div className="font-display font-bold flex items-center gap-2"><Plus className="h-4 w-4" /> Neue Gruppe gründen</div>
                    <div className="text-xs text-muted-foreground mt-1">Du wirst Admin und kannst Freunde einladen</div>
                  </button>
                  <button onClick={()=>setStep(3)} className="text-xs text-muted-foreground underline mt-2 tap">Überspringen</button>
                </div>
              )}

              {!urlCode && groupMode === "join" && (
                <div className="space-y-3">
                  <Input value={groupCode} onChange={(e)=>setGroupCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,6))} placeholder="z.B. K7M2X9" className="h-14 text-2xl font-mono font-bold tracking-[0.3em] text-center" maxLength={6} />
                  {groupCheck && groupCode.length === 6 && <p className="text-xs text-muted-foreground">Prüfe Code …</p>}
                  {groupError && <p className="text-xs text-destructive">{groupError}</p>}
                  {groupPreview && (
                    <div className="rounded-md bg-accent/20 border-2 border-accent px-3 py-3 text-sm flex items-center gap-3">
                      <GroupLogo url={groupPreview.logo_url} size={40} />
                      <div>
                        <div className="font-bold">{groupPreview.name}</div>
                        <div className="text-xs text-muted-foreground">{groupPreview.member_count} Mitglieder</div>
                      </div>
                      <Check className="h-5 w-5 text-success ml-auto" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={()=>{ setGroupMode(null); setGroupCode(""); }} className="flex-1 h-12">← Zurück</Button>
                    <Button onClick={submitGroup} disabled={busy || !groupPreview} className="flex-1 h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                      {busy ? "Tritt bei …" : "Beitreten 🎯"}
                    </Button>
                  </div>
                </div>
              )}

              {groupMode === "create" && (
                <div className="space-y-3">
                  <Input value={newGroupName} onChange={(e)=>setNewGroupName(e.target.value)} placeholder='Gruppenname (z.B. „Büro-Liga 2026")' className="h-14 text-base font-semibold" maxLength={50} />
                  {newGroupName.trim().length >= 2 && (
                    groupNameStatus === "checking" ? <p className="text-xs text-muted-foreground">Prüfe Verfügbarkeit …</p>
                    : groupNameStatus === "taken" ? <p className="text-xs text-destructive">❌ Gruppenname bereits vergeben</p>
                    : groupNameStatus === "ok" ? <p className="text-xs text-success">✅ Verfügbar</p>
                    : null
                  )}
                  <p className="text-xs text-muted-foreground">Du wirst Admin und kannst danach ein Logo hochladen sowie Freunde einladen.</p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={()=>{ setGroupMode(null); setNewGroupName(""); }} className="flex-1 h-12">← Zurück</Button>
                    <Button onClick={submitGroup} disabled={busy || newGroupName.trim().length < 2 || groupNameStatus !== "ok"} className="flex-1 h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                      {busy ? "Erstelle …" : "Gruppe erstellen 🚀"}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="3" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}} className="space-y-4">
              <h1 className="font-display font-extrabold text-3xl">Dein Avatar 🎯</h1>
              <p className="text-muted-foreground">Wähle ein Emoji oder lade ein eigenes Foto hoch.</p>

              <div className="flex justify-center">
                <UserAvatar url={avatarTab === "photo" ? avatarUrl : null} emoji={emoji} size={96} />
              </div>

              <div className="bg-muted rounded-pill p-1 flex">
                <button onClick={()=>setAvatarTab("emoji")} className={`flex-1 text-xs font-bold py-2 rounded-pill tap transition ${avatarTab === "emoji" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}>😀 Emoji</button>
                <button onClick={()=>setAvatarTab("photo")} className={`flex-1 text-xs font-bold py-2 rounded-pill tap transition ${avatarTab === "photo" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}>📸 Foto</button>
              </div>

              {avatarTab === "emoji" && (
                <div className="grid grid-cols-4 gap-3">
                  {EMOJIS.map((e) => (
                    <button key={e} type="button" onClick={()=>setEmoji(e)} className={`aspect-square rounded-lg text-4xl tap transition-all ${emoji === e ? "bg-accent shadow-glow scale-105" : "bg-card shadow-card hover:bg-muted"}`}>{e}</button>
                  ))}
                </div>
              )}

              {avatarTab === "photo" && (
                <div className="space-y-2">
                  <button
                    onClick={()=>fileRef.current?.click()}
                    disabled={busy}
                    className="w-full h-14 rounded-lg border-2 border-dashed border-accent/60 bg-card flex items-center justify-center gap-2 font-bold tap hover:bg-muted transition disabled:opacity-50"
                  >
                    <Upload className="h-5 w-5" />
                    {busy ? "Lade hoch …" : avatarUrl ? "Anderes Foto wählen" : "Foto hochladen (max. 3 MB)"}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file || !user) return;
                      if (file.size > MAX_AVATAR_BYTES) return toast.error("Bild zu groß (max. 3 MB)");
                      setBusy(true);
                      try {
                        const url = await uploadAvatar(user.id, file);
                        setAvatarUrl(url);
                        await refreshProfile();
                        toast.success("Foto hochgeladen ✅");
                      } catch (err: any) { toast.error(err.message); }
                      setBusy(false);
                    }}
                  />
                  <p className="text-[11px] text-muted-foreground text-center">
                    Dein Emoji wird als Fallback gespeichert — falls du das Foto später entfernst.
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={()=>setStep(2)} className="flex-1 h-12">← Zurück</Button>
                <Button onClick={()=>setStep(4)} className="flex-1 h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">Weiter →</Button>
              </div>
            </motion.div>
          )}

          {step === 4 && (() => {
            const champTeams = Object.values(groupTeams).flat()
              .reduce<Map<string, string>>((m, t) => { m.set(t.name, t.flag); return m; }, new Map());
            const champList = Array.from(champTeams.entries()).sort((a, b) => a[0].localeCompare(b[0]));
            const groupKeys = Object.keys(groupTeams).sort();
            return (
            <motion.div key="4" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}} className="space-y-4">
              <h1 className="font-display font-extrabold text-3xl">Deine Bonus-Fragen 🌍</h1>
              <p className="text-muted-foreground">Sammle bis zu <b>+{BONUS_MAX_POINTS} Punkte</b> für deine Vorhersagen.</p>

              {bonusLocked && (
                <div className="rounded-lg p-3 bg-muted border border-border flex items-center gap-2 text-sm">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-bold">Bonus-Fragen seit WM-Start gesperrt</span>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">🏆 Weltmeister · +20 Pkt</label>
                <select value={worldChamp} onChange={(e)=>setWorldChamp(e.target.value)} disabled={bonusLocked} className="w-full h-12 px-3 rounded-lg border border-input bg-card text-base font-semibold disabled:opacity-50">
                  <option value="">— Team wählen —</option>
                  {/* Value stays the English name (it's the canonical key from
                      API-Football); only the displayed label is German. */}
                  {champList.map(([name, flag]) => <option key={name} value={name}>{flag} {teamDe(name)}</option>)}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">⚽ Torschützenkönig · +15 Pkt</label>
                <PlayerCombobox value={topScorer} onChange={setTopScorer} disabled={bonusLocked} />
              </div>

              <details className="rounded-lg border border-border bg-card p-3">
                <summary className="text-sm font-bold cursor-pointer">🥇 Gruppensieger · je +5 Pkt (optional)</summary>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {groupKeys.map(g => (
                    <div key={g}>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Gruppe {g}</label>
                      <select value={groupWinners[g] ?? ""} onChange={(e)=>setGroupWinners({...groupWinners, [g]: e.target.value})} disabled={bonusLocked} className="w-full h-10 px-2 rounded-md border border-input bg-background text-xs disabled:opacity-50">
                        <option value="">—</option>
                        {(groupTeams[g] ?? []).map(t => <option key={t.name} value={t.name}>{t.flag} {teamDe(t.name)}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </details>

              {/* Austria-Spezial — collapsible like the Gruppensieger block,
                  same 6 questions as on the Bonus page so signups don't miss
                  the extra +30 Pkt available. Pre-filling is optional —
                  users can edit later via Profil → Bonus-Fragen. */}
              <details className="rounded-lg border-2 border-red-600/30 bg-card p-3">
                <summary className="text-sm font-bold cursor-pointer">🇦🇹 Österreich-Spezial · 6 Fragen, je +5 Pkt (optional)</summary>
                <div className="space-y-3 mt-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Kommt AT weiter als Deutschland?</label>
                    <select value={atBetterThanDe} onChange={(e) => setAtBetterThanDe(e.target.value as "" | "yes" | "no")} disabled={bonusLocked} className="w-full h-10 px-2 rounded-md border border-input bg-background text-xs disabled:opacity-50 mt-1">
                      <option value="">— wählen —</option>
                      <option value="yes">Ja</option>
                      <option value="no">Nein</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Gruppenphase-Platz Österreich</label>
                    <select value={atGroupPosition} onChange={(e) => setAtGroupPosition(e.target.value as "" | "1" | "2" | "3" | "4")} disabled={bonusLocked} className="w-full h-10 px-2 rounded-md border border-input bg-background text-xs disabled:opacity-50 mt-1">
                      <option value="">— wählen —</option>
                      <option value="1">1. Platz</option>
                      <option value="2">2. Platz</option>
                      <option value="3">3. Platz</option>
                      <option value="4">4. Platz</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Erstes AT-Tor (Spieler)</label>
                    <PlayerCombobox value={atFirstGoalScorer} onChange={setAtFirstGoalScorer} disabled={bonusLocked} team="Austria" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Bester AT-Torschütze</label>
                    <PlayerCombobox value={atTopScorer} onChange={setAtTopScorer} disabled={bonusLocked} team="Austria" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Wie weit kommt Österreich?</label>
                    <select value={atProgression} onChange={(e) => setAtProgression(e.target.value as AtProgression | "")} disabled={bonusLocked} className="w-full h-10 px-2 rounded-md border border-input bg-background text-xs disabled:opacity-50 mt-1">
                      <option value="">— wählen —</option>
                      {AT_PROGRESSION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">AT-Tore gesamt (ohne Elfmeterschießen)</label>
                    <input
                      type="number"
                      min={0}
                      max={99}
                      value={atGoalsTotal}
                      onChange={(e) => setAtGoalsTotal(e.target.value)}
                      disabled={bonusLocked}
                      placeholder="z. B. 7"
                      className="w-full h-10 px-2 rounded-md border border-input bg-background text-xs disabled:opacity-50 mt-1 tabular-nums"
                    />
                  </div>
                </div>
              </details>

              <div className="flex gap-2">
                <Button variant="outline" onClick={()=>setStep(3)} className="flex-1 h-12">← Zurück</Button>
                <Button disabled={!worldChamp || busy} onClick={finish} className="flex-1 h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                  {busy ? "Speichere …" : "Los geht's! 🚀"}
                </Button>
              </div>
            </motion.div>
            );
          })()}
        </AnimatePresence>
      </div>
    </div>
  );
}
